import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, createAppAsyncThunk } from "appThunk";
import { setupCube } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { RootState } from "store";
import { loadStores, Store } from "./store";
import { loadKPMGSpendCategories } from "./kpmgSpendCategory";
import { loadClientRegistration } from "./clientRegistration";
import { loadClientFeatureNames, ClientFeatureName } from "./clientFeatureName";
import { backdropOff, backdropOn } from "modules/backdrop/backdropSlice";
import { CatchmentCustomerProfiles, loadCatchmentCustomerProfiles } from "./catchmentCustomerProfile";
import { SortDirection, stringSortExpression } from "utils/sortUtils";
import { GridColDef } from "@mui/x-data-grid-pro";
import numberFormatter from "utils/numberFormatter";
import { loadCostReferenceDate } from "./costsReferenceDate";
import { loadYearlyCosts, YearlyCosts } from "./yearlyCosts";
import { CatchmentSpend, loadCatchmentSpend } from "./catchmentSpend";
import { ForecastSales, loadForecastSales } from "./forecastSales";

interface LoadReportResponse {
    stores: Store[],
    forecastSales: ForecastSales[],
    yearlyCosts: YearlyCosts[],
    catchmentSpend: CatchmentSpend[],
    catchmentCustomerProfiles: CatchmentCustomerProfiles[],
    clientFeatureNames: ClientFeatureName[]
}

interface profitDrivers {
    storeId: string,
    propertyCostsPerSquareFoot?: number,
    revenuePerPoundOfPropertyCost?: number,
    payrollCostsPerSquareFoot?: number,
    revenuePerPoundOfPayrollCost?: number
}

interface ReportState {
    isLoading: boolean,
    hasErrors: boolean,
    stores: Store[],
    forecastSales: ForecastSales[],
    yearlyCosts: YearlyCosts[],
    catchmentSpend: CatchmentSpend[],
    catchmentCustomerProfiles: CatchmentCustomerProfiles[],
    clientFeatureNames: ClientFeatureName[]
}

const initialState: ReportState = {
    isLoading: false,
    hasErrors: false,
    stores: [],
    forecastSales: [],
    yearlyCosts: [],
    catchmentSpend: [],
    catchmentCustomerProfiles: [],
    clientFeatureNames: []
};

const reportSlice = createSlice({
    name: "customer/tools/report",
    initialState,
    reducers: {
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadReport.pending, (state: ReportState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.stores = [];
            state.forecastSales = [];
            state.yearlyCosts = [];
            state.catchmentSpend = [];
            state.catchmentCustomerProfiles = [];
            state.clientFeatureNames = [];
        });
        builder.addCase(loadReport.rejected, (state: ReportState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.stores = [];
            state.forecastSales = [];
            state.yearlyCosts = [];
            state.catchmentSpend = [];
            state.catchmentCustomerProfiles = [];
            state.clientFeatureNames = [];
        });
        builder.addCase(loadReport.fulfilled, (state: ReportState, action: PayloadAction<LoadReportResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.stores = action.payload.stores;
            state.forecastSales = action.payload.forecastSales;
            state.yearlyCosts = action.payload.yearlyCosts;
            state.catchmentSpend = action.payload.catchmentSpend;
            state.catchmentCustomerProfiles = action.payload.catchmentCustomerProfiles;
            state.clientFeatureNames = action.payload.clientFeatureNames;
        });
    }
});

export const loadReport = createAppAsyncThunk(
    "customer/tools/report/loadReport",
    async (arg, thunkAPI) => {
        thunkAPI.dispatch(backdropOn());
        try {
            await thunkAPI.dispatch(setupCube());
            const storesPromise = thunkAPI.dispatch(loadStores());
            const forecastSalesPromise = thunkAPI.dispatch(loadForecastSales());
            const clientRegistrationPromise = thunkAPI.dispatch(loadClientRegistration());
            const kpmgSpendCategoriesPromise = thunkAPI.dispatch(loadKPMGSpendCategories());
            const costsReferenceDatePromise = thunkAPI.dispatch(loadCostReferenceDate());
            const clientFeatureNamesPromise = thunkAPI.dispatch(loadClientFeatureNames());

            const setupResults = await Promise.all([
                storesPromise,
                forecastSalesPromise,
                clientRegistrationPromise,
                kpmgSpendCategoriesPromise,
                costsReferenceDatePromise,
                clientFeatureNamesPromise
            ]);

            const stores = setupResults[0];
            const forecastSales = setupResults[1];
            const clientRegistration = setupResults[2];
            const kpmgSpendCategories = setupResults[3];
            const costsReferenceDate = setupResults[4];
            const clientFeatureNames = setupResults[5];

            const yearlyCostsPromise = thunkAPI.dispatch(loadYearlyCosts(costsReferenceDate, stores.length));
            const catchmentSpendPromise = thunkAPI.dispatch(loadCatchmentSpend(stores, clientRegistration.accountId, kpmgSpendCategories));
            const catchmentCustomerProfilesPromise = thunkAPI.dispatch(loadCatchmentCustomerProfiles(stores.length));

            const insightResults = await Promise.all([
                yearlyCostsPromise,
                catchmentSpendPromise,
                catchmentCustomerProfilesPromise
            ]);
            const yearlyCosts = insightResults[0];
            const catchmentSpend = insightResults[1];
            const catchmentCustomerProfiles = insightResults[2];

            const loadReportResponse: LoadReportResponse = {
                stores,
                forecastSales,
                yearlyCosts,
                catchmentSpend,
                catchmentCustomerProfiles,
                clientFeatureNames
            };
            return loadReportResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Report.", error));
            return thunkAPI.rejectWithValue(null);
        } finally {
            thunkAPI.dispatch(backdropOff());
        }
    }
);

export const clearReport = (): AppThunk => (dispatch) => {
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.tools.report.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.tools.report.hasErrors;
};

export const selectStores = (state: RootState) => {
    return state.customer.tools.report.stores;
};

export const selectForecastSales = (state: RootState) => {
    return state.customer.tools.report.forecastSales;
};

export const selectYearlyCosts = (state: RootState) => {
    return state.customer.tools.report.yearlyCosts;
};

export const selectCatchmentSpend = (state: RootState) => {
    return state.customer.tools.report.catchmentSpend;
};

export const selectCatchmentCustomerProfiles = (state: RootState) => {
    return state.customer.tools.report.catchmentCustomerProfiles;
};

export const selectClientFeatureNames = (state: RootState) => {
    return state.customer.tools.report.clientFeatureNames;
};

const arrayJoinOnKey = (arrays: any[][], joinKey: string) => {
    if (arrays.length === 0) {
        return [];
    } else if (arrays.length === 1) {
        return arrays[0];
    }

    const sortedArrays = arrays.map(array =>
        [...array].sort((a, b) => stringSortExpression(a[joinKey] ?? '', b[joinKey] ?? '', SortDirection.ASC))
    );
    const arrayIndexes = arrays.map(array => 0);

    return sortedArrays[0].map(baseRow => {
        let joinedRow = baseRow;
        for (let i = 1; i < sortedArrays.length; i++) {
            let currentArray = sortedArrays[i];
            let joiningRow = currentArray[arrayIndexes[i]];

            if (baseRow[joinKey] !== (joiningRow ? joiningRow[joinKey] : undefined)) {
                let relevantRowIndex = currentArray.findIndex(item => item[joinKey] === baseRow[joinKey]);
                joiningRow = currentArray[relevantRowIndex];
            }
            arrayIndexes[i]++;
            joinedRow = Object.assign({}, joinedRow, joiningRow);
        }
        return joinedRow;
    });
};

export const selectProfitDrivers = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectStores,
    selectYearlyCosts,
    (isLoading, hasErrors, stores, yearlyCosts) => {
        if (isLoading || hasErrors) {
            return [];
        }

        return stores.map(store => {
            const profitDrivers: profitDrivers = {
                storeId: store.id
            };

            const costs = yearlyCosts.find(item => item.storeId === store.id);
            if (costs) {
                const propertykeys = Object.keys(costs).filter(key => key.includes("Property"));
                let propertyCosts = 0;
                propertykeys.forEach(key => {
                    propertyCosts += Number(costs[key]);
                });

                const payrollCosts = Number(costs["Labour"]);
                profitDrivers.propertyCostsPerSquareFoot = (!store.sizeInSquareFeet || !propertyCosts) ? undefined : propertyCosts / store.sizeInSquareFeet;
                profitDrivers.revenuePerPoundOfPropertyCost = !propertyCosts ? undefined : costs.revenue / propertyCosts;
                profitDrivers.payrollCostsPerSquareFoot = (!store.sizeInSquareFeet || !payrollCosts) ? undefined : payrollCosts / store.sizeInSquareFeet;
                profitDrivers.revenuePerPoundOfPayrollCost = !payrollCosts ? undefined : costs.revenue / payrollCosts;
            }
            return profitDrivers;
        });
    }
);

export const selectStoresWithAllMetrics = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectStores,
    selectForecastSales,
    selectYearlyCosts,
    selectProfitDrivers,
    selectCatchmentSpend,
    selectCatchmentCustomerProfiles,
    (isLoading, hasErrors, stores, forecastSales, yearlyCosts, profitDrivers, catchmentSpend, catchmentCustomerProfiles) => {
        if (isLoading || hasErrors) {
            return [];
        }
        const storesWithStoreIds = stores.map(store => ({
            storeId: store.id,
            ...store
        }));
        const storesWithAllMetrics = arrayJoinOnKey([storesWithStoreIds, forecastSales, yearlyCosts, profitDrivers, catchmentSpend, catchmentCustomerProfiles], "storeId");
        return storesWithAllMetrics.sort((a, b) => stringSortExpression(a.name, b.name, SortDirection.ASC));
    }
);

export const selectCatchmentCustomerProfilesColumns = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectCatchmentCustomerProfiles,
    (isLoading, hasErrors, catchmentCustomerProfiles) => {
        if (isLoading || hasErrors || catchmentCustomerProfiles.length === 0) {
            return [];
        }
        const keys = Object.keys(catchmentCustomerProfiles[0]).filter(key => key !== "storeId");
        const columns: GridColDef[] = keys.map(key => ({
            field: key,
            type: 'number',
            flex: 1,
            headerName: key,
            sortable: true,
            valueGetter: (params) => params.value ?? 0,
            renderCell: (params) => numberFormatter.toDecimalPlaces(params.value ?? 0, 1),
        }));
        return columns;
    }
);

export const selectCostsColumns = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectYearlyCosts,
    (isLoading, hasErrors, yearlyCosts) => {
        if (isLoading || hasErrors || yearlyCosts.length === 0) {
            return [];
        }
        const keys = Object.keys(yearlyCosts[0]).filter(key => !(["storeId", "revenue"].includes(key)));
        const columns: GridColDef[] = keys.map(key => ({
            field: key,
            type: 'number',
            flex: 1,
            headerName: key,
            sortable: true,
            valueGetter: (params) => params.value ?? 0,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
        }));
        return columns;
    }
);

export const selectProfitDriversColumns = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectProfitDrivers,
    (isLoading, hasErrors, profitDrivers) => {
        if (isLoading || hasErrors || profitDrivers.length === 0) {
            return [];
        }
        const columns: GridColDef[] = [];
        const columnTemplate: GridColDef = {
            field: "Field name",
            type: 'number',
            flex: 1,
            headerName: "Header name",
            sortable: true,
            hide: true,
            valueGetter: (params) => params.value ?? 0,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
        };
        if (profitDrivers.some(item => item.propertyCostsPerSquareFoot)) {
            columns.push({
                ...columnTemplate,
                field: 'propertyCostsPerSquareFoot',
                headerName: 'Property costs per square foot'
            });
        }
        if (profitDrivers.some(item => item.revenuePerPoundOfPropertyCost)) {
            columns.push({
                ...columnTemplate,
                field: 'revenuePerPoundOfPropertyCost',
                headerName: 'Revenue per £ of property cost',
            });
        }
        if (profitDrivers.some(item => item.payrollCostsPerSquareFoot)) {
            columns.push({
                ...columnTemplate,
                field: 'payrollCostsPerSquareFoot',
                headerName: 'Payroll costs per square foot',
            });
        }
        if (profitDrivers.some(item => item.revenuePerPoundOfPayrollCost)) {
            columns.push({
                ...columnTemplate,
                field: 'revenuePerPoundOfPayrollCost',
                headerName: 'Revenue per £ of property cost',
            });
        }
        return columns;
    }
);

export const selectClientCategoryFeatureColumns = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectClientFeatureNames,
    (isLoading, hasErrors, clientFeatureNames) => {
        if (isLoading || hasErrors || clientFeatureNames.length === 0) {
            return [];
        }
        const columns: GridColDef[] = [];
        const defaultFeatureNames = [
            "ClientCategoryFeature1",
            "ClientCategoryFeature2",
            "ClientCategoryFeature3",
            "ClientCategoryFeature4",
            "ClientCategoryFeature5"
        ];
        const clientFeatureDictionary = Object.fromEntries(clientFeatureNames.map(item => [item.DefaultFeatureName, item.ClientFeatureName]));

        columns.push(
            ...defaultFeatureNames
                .filter(item => item in clientFeatureDictionary)
                .map(item => ({
                    field: item,
                    headerName: clientFeatureDictionary[item],
                    type: 'singleSelect',
                    flex: 1,
                    sortable: true,
                    hide: true,
                }))
        );

        return columns;
    }
);

export const selectClientNumericalFeatureColumns = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectClientFeatureNames,
    (isLoading, hasErrors, clientFeatureNames) => {
        if (isLoading || hasErrors || clientFeatureNames.length === 0) {
            return [];
        }
        const columns: GridColDef[] = [];
        const defaultFeatureNames = [
            "ClientNumericalFeature1",
            "ClientNumericalFeature2",
            "ClientNumericalFeature3",
            "ClientNumericalFeature4",
            "ClientNumericalFeature5",
            "ClientNumericalFeature6",
            "ClientNumericalFeature7",
            "ClientNumericalFeature8",
            "ClientNumericalFeature9",
            "ClientNumericalFeature10",
            "ClientNumericalFeature11",
            "ClientNumericalFeature12",
            "ClientNumericalFeature13",
            "ClientNumericalFeature14",
            "ClientNumericalFeature15",
        ];
        const clientFeatureDictionary = Object.fromEntries(clientFeatureNames.map(item => [item.DefaultFeatureName, item.ClientFeatureName]));

        columns.push(
            ...defaultFeatureNames
                .filter(item => item in clientFeatureDictionary)
                .map(item => ({
                    field: item,
                    headerName: clientFeatureDictionary[item],
                    type: 'number',
                    flex: 1,
                    sortable: true,
                    hide: true,
                }))
        );

        return columns;
    }
);

export default reportSlice;
